import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { isInvertedRange } from '@popety-io/utils'
import { Icon } from '../../atoms/Icon'
import { ToggleSwitch } from '../../atoms/ToggleSwitch'
import { useFilterSectionContext } from './FilterSectionContext'

// Search Input
const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 32px 8px ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 12px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.default};

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

const SearchIconWrapper = styled.div<{ $clickable?: boolean }>`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.tertiary};
  pointer-events: ${({ $clickable }) => ($clickable ? 'auto' : 'none')};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    color: ${({ $clickable, theme }) =>
      $clickable ? theme.colors.text.primary : theme.colors.text.tertiary};
  }
`

export const FilterSearch = React.forwardRef<
  HTMLInputElement,
  {
    placeholder: string
    value: string
    onChange: (value: string) => void
    onKeyDown?: (e: React.KeyboardEvent) => void
  }
>(function FilterSearch({ placeholder, value, onChange, onKeyDown }, ref) {
  const hasValue = value.length > 0

  return (
    <SearchWrapper>
      <SearchInput
        ref={ref}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <SearchIconWrapper
        $clickable={hasValue}
        onClick={hasValue ? () => onChange('') : undefined}
      >
        <Icon name={hasValue ? 'x' : 'search'} size="small" />
      </SearchIconWrapper>
    </SearchWrapper>
  )
})

// Select Dropdown
const SelectWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const SelectInput = styled.select<{ $hasValue?: boolean }>`
  width: 100%;
  padding: 8px 28px 8px 10px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $hasValue }) =>
      $hasValue ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 11px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  appearance: none;

  &:hover {
    border-color: ${({ theme, $hasValue }) =>
      $hasValue ? theme.colors.primaryHover : theme.colors.text.tertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }
`

const SelectArrow = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const FilterSelect: React.FC<{
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
}> = ({ value, onChange, options, placeholder = 'Select...' }) => (
  <SelectWrapper>
    <SelectInput
      value={value}
      onChange={(e) => onChange(e.target.value)}
      $hasValue={!!value}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </SelectInput>
    <SelectArrow>
      <Icon name="arrow-down" size="small" />
    </SelectArrow>
  </SelectWrapper>
)

// Input Group
const InputGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`

const InputLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

const InputField = styled.input<{ $hasValue?: boolean; $hasError?: boolean }>`
  width: 100%;
  padding: 6px 0px 6px 10px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $hasValue, $hasError }) =>
      $hasError
        ? theme.colors.border.error
        : $hasValue
          ? theme.colors.primary
          : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 11px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme, $hasValue, $hasError }) =>
      $hasError
        ? theme.colors.border.error
        : $hasValue
          ? theme.colors.primaryHover
          : theme.colors.border.focus};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.border.error : theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${({ theme, $hasError }) =>
        $hasError ? `${theme.colors.warning}33` : `${theme.colors.primary}1A`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

const InputSuffix = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 10px;
  pointer-events: none;
`

const formatSwissNumber = (value: string): string => {
  if (!value) return ''
  const raw = value.replace(/['\s]/g, '')
  const parts = raw.split('.')
  const intPart = parts[0]
  if (!intPart || isNaN(Number(intPart))) return value
  const formatted = Number(intPart).toLocaleString('de-CH')
  return parts.length > 1 ? `${formatted}.${parts[1]}` : formatted
}

const NumericInput: React.FC<{
  placeholder: string
  value: string
  onChange: (value: string) => void
  hasValue: boolean
  hasError?: boolean
}> = ({ placeholder, value, onChange, hasValue, hasError }) => {
  const [focused, setFocused] = React.useState(false)
  const [localValue, setLocalValue] = React.useState(value)

  // Sync from parent when not focused
  React.useEffect(() => {
    if (!focused) setLocalValue(value)
  }, [value, focused])

  return (
    <InputField
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={focused ? localValue : formatSwissNumber(value)}
      onChange={(e) => {
        const raw = e.target.value.replace(/'/g, '')
        setLocalValue(raw)
        onChange(raw)
      }}
      onFocus={() => {
        setFocused(true)
        setLocalValue(value)
      }}
      onBlur={() => setFocused(false)}
      $hasValue={hasValue}
      $hasError={hasError}
    />
  )
}

const RangeWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xsmall};
  margin: -${({ theme }) => theme.spacing.small} 0
    ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  /* The warning tint carries the semantics; the text must stay on the
     high-contrast neutral - warning on warningLight is ~1.5:1 and
     unreadable in both palettes. */
  background: ${({ theme }) => theme.colors.warningLight};
  border-left: 3px solid ${({ theme }) => theme.colors.warning};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 10px;
  line-height: 1.3;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`

/**
 * A min/max pair is always rendered as a single two-input group, so an
 * inverted range can be detected here — every filter panel gets the warning
 * without touching a single call site.
 */
const isInvertedInputPair = (
  inputs: Array<{ value: string; type?: string }>
): boolean => {
  if (inputs.length !== 2) return false

  const isRangeType = (type?: string) =>
    type === 'number' || type === 'integer' || type === 'date'

  const [min, max] = inputs
  if (!isRangeType(min.type) || !isRangeType(max.type)) return false
  if (min.type !== max.type) return false

  return isInvertedRange({ min: min.value, max: max.value })
}

export const FilterInputGroup: React.FC<{
  inputs: Array<{
    label?: string
    placeholder: string
    value: string
    onChange: (value: string) => void
    suffix?: string
    type?: string
    step?: string | number
  }>
}> = ({ inputs }) => {
  const { t } = useTranslation('ui')
  const isInverted = isInvertedInputPair(inputs)

  return (
    <>
      <InputGroup>
        {inputs.map((input, index) => {
          const isNumeric = input.type === 'number' || input.type === 'integer'
          const isDate = input.type === 'date'
          return (
            <InputWrapper key={index}>
              {input.label && <InputLabel>{input.label}</InputLabel>}
              {isNumeric ? (
                <NumericInput
                  placeholder={input.placeholder}
                  value={input.value}
                  onChange={input.onChange}
                  hasValue={!!input.value}
                  hasError={isInverted}
                />
              ) : (
                <InputField
                  type={isDate ? 'date' : 'text'}
                  placeholder={input.placeholder}
                  value={input.value}
                  onChange={(e) => input.onChange(e.target.value)}
                  $hasValue={!!input.value}
                  $hasError={isInverted}
                />
              )}
              {input.suffix && <InputSuffix>{input.suffix}</InputSuffix>}
            </InputWrapper>
          )
        })}
      </InputGroup>
      {isInverted && (
        <RangeWarning role="alert">
          <Icon name="alert-triangle" size="xs" />
          {t('filters.filterPanel.invalidRange')}
        </RangeWarning>
      )}
    </>
  )
}

// Radio Group
const RadioGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
`

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  user-select: none;
  font-size: 10px;
`

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`

const RadioBox = styled.span<{ checked: boolean }>`
  width: 12px;
  height: 12px;
  border: 2px solid
    ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.border.default};
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing.xsmall};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.text.tertiary};
  }

  ${({ checked, theme }) =>
    checked &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 6px;
      height: 6px;
      background: ${theme.colors.primary};
      border-radius: 50%;
    }
  `}
`

const RadioLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`

export const FilterRadioGroup: React.FC<{
  name: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}> = ({ name, value, onChange, options }) => (
  <RadioGroup>
    {options.map((option) => (
      <RadioItem key={option.value}>
        <RadioInput
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={(e) => onChange(e.target.value)}
        />
        <RadioBox checked={value === option.value} />
        <RadioLabel>{option.label}</RadioLabel>
      </RadioItem>
    ))}
  </RadioGroup>
)

// Checkbox Group
const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  user-select: none;
  font-size: 11px;
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
`

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`

const CheckboxBox = styled.span<{ checked: boolean }>`
  width: 15px;
  height: 15px;
  border: 2px solid
    ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-right: ${({ theme }) => theme.spacing.small};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};
  background: ${({ checked, theme }) =>
    checked ? theme.colors.primary : 'transparent'};

  &:hover {
    border-color: ${({ checked, theme }) =>
      checked ? theme.colors.primary : theme.colors.text.tertiary};
  }

  ${({ checked }) =>
    checked &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 2px;
      width: 3px;
      height: 6px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  `}
`

const CheckboxLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`

export const FilterCheckboxGroup: React.FC<{
  options: Array<{
    value: string
    label: string
    checked: boolean
    onChange: (checked: boolean) => void
  }>
}> = ({ options }) => (
  <CheckboxGroup>
    {options.map((option) => (
      <CheckboxItem key={option.value}>
        <CheckboxInput
          type="checkbox"
          checked={option.checked}
          onChange={(e) => option.onChange(e.target.checked)}
        />
        <CheckboxBox checked={option.checked} />
        <CheckboxLabel>{option.label}</CheckboxLabel>
      </CheckboxItem>
    ))}
  </CheckboxGroup>
)

// Zone Type Label
export const ZoneTypeLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

// Zone Type Group
export const ZoneTypeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`

// Filter Subsection
export const FilterSubsection = styled.div.attrs({
  // styled-components v6 type-checks `.attrs` object keys against the element's
  // props, which don't include custom data-* attributes; cast so this (runtime-
  // valid, selector-relied-upon) data attribute type-checks.
  'data-filter-subsection': true,
} as React.HTMLAttributes<HTMLDivElement>)`
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  &:last-child {
    margin-bottom: 0;
  }
`

const HighlightPulse = styled.div<{ $active: boolean }>`
  border-radius: 4px;
  margin: -4px -6px;
  padding: 4px 6px;
  background: ${({ $active }) => ($active ? '#fef3c7' : 'transparent')};
  transition: background 1.5s ease-out;
`

// Field Label (styled base)
const FieldLabelStyled = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

// Field Label with highlight support
export const FieldLabel: React.FC<{
  label?: string
  children?: React.ReactNode
}> = ({ label, children }) => {
  const { highlightField, onHighlightComplete } = useFilterSectionContext()
  const containerRef = React.useRef<HTMLDivElement>(null)

  const matchText = label || (typeof children === 'string' ? children : null)
  const isTarget = matchText != null && highlightField === matchText

  React.useEffect(() => {
    if (isTarget && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      // Auto-focus the first input/select after scroll + section expansion completes
      const focusTimer = setTimeout(() => {
        const parent = containerRef.current?.closest('[data-filter-subsection]')
        if (parent) {
          const focusable = parent.querySelector(
            'input, select, [tabindex]'
          ) as HTMLElement
          if (focusable) focusable.focus()
        }
      }, 600)
      const timer = setTimeout(() => {
        onHighlightComplete()
      }, 2000)
      return () => {
        clearTimeout(focusTimer)
        clearTimeout(timer)
      }
    }
  }, [isTarget, onHighlightComplete])

  return (
    <HighlightPulse ref={containerRef} $active={isTarget}>
      <FieldLabelStyled>{children || label}</FieldLabelStyled>
    </HighlightPulse>
  )
}

// Field Label with Tooltip
const FieldLabelWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const FieldLabelInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`

const FieldLabelText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`

const InfoIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: help;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 173, 230, 0.1);
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 8px;
    height: 8px;
  }
`

const TooltipBox = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.98);
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 5px;
  font-size: 10px;
  line-height: 1.6;
  font-weight: 400;
  white-space: pre-line;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transform: translateY(${({ $isVisible }) => ($isVisible ? '0' : '-4px')});
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
`

export const FieldLabelWithTooltip: React.FC<{
  label: string
  tooltip?: string
}> = ({ label, tooltip }) => {
  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { highlightField, onHighlightComplete } = useFilterSectionContext()

  const isTarget = highlightField === label

  React.useEffect(() => {
    if (isTarget && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      const timer = setTimeout(() => {
        onHighlightComplete()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [isTarget, onHighlightComplete])

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsTooltipVisible(true)
    }, 100)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsTooltipVisible(false)
  }

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!tooltip) {
    return (
      <HighlightPulse ref={containerRef} $active={isTarget}>
        <FieldLabelStyled>{label}</FieldLabelStyled>
      </HighlightPulse>
    )
  }

  return (
    <HighlightPulse ref={containerRef} $active={isTarget}>
      <FieldLabelWrapper>
        <FieldLabelInner>
          <FieldLabelText>{label}</FieldLabelText>
          <InfoIconWrapper
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Icon name="info" size="xs" />
          </InfoIconWrapper>
        </FieldLabelInner>
        <TooltipBox $isVisible={isTooltipVisible}>{tooltip}</TooltipBox>
      </FieldLabelWrapper>
    </HighlightPulse>
  )
}

// Filter Sub-Group Header (visual header within a FilterSection)
const SubGroupHeaderWrapper = styled.div<{ $isFirst?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding-top: ${({ theme, $isFirst }) =>
    $isFirst ? '0' : theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  ${({ $isFirst }) =>
    !$isFirst &&
    `
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  `}
`

const SubGroupLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const FilterSubGroupHeader: React.FC<{
  label: string
  isFirst?: boolean
}> = ({ label, isFirst }) => (
  <SubGroupHeaderWrapper $isFirst={isFirst}>
    <SubGroupLabel>{label}</SubGroupLabel>
  </SubGroupHeaderWrapper>
)

// Single Checkbox
const SingleCheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  padding: ${({ theme }) => theme.spacing.xsmall} 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

const SingleCheckboxInput = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`

export const FilterCheckbox: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
}> = ({ checked, onChange, label }) => (
  <SingleCheckboxWrapper>
    <SingleCheckboxInput
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    {label}
  </SingleCheckboxWrapper>
)

// Checkbox Container
export const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

// Toggle Item
const ToggleItemWrapper = styled.label`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.medium};
  cursor: pointer;
  user-select: none;
`

const ToggleLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-left: ${({ theme }) => theme.spacing.small};
`

export const FilterToggle: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  disabled?: boolean
}> = ({ checked, onChange, label, disabled }) => (
  <ToggleItemWrapper>
    <ToggleSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      size="medium"
    />
    <ToggleLabel>{label}</ToggleLabel>
  </ToggleItemWrapper>
)
